import React, { useState } from 'react';
import { X, Clock, FileText, Brain, Target, TrendingUp, ListChecks, Search, Database, UserPlus, BarChart, CheckCircle, Shield, Flame, Sun, Snowflake, RefreshCw, Layers } from 'lucide-react';

interface LeadGenHowIWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'process' | 'behavior' | 'intelligence';

const processSteps = [
  {
    icon: Target,
    title: 'Analyze ICP & Buyer Persona',
    description: 'I study your ideal customer profile, target industries, company sizes, and decision-maker titles to understand exactly who you want to reach.',
  },
  {
    icon: Database,
    title: 'Crawl Verified Databases',
    description: 'I search through verified databases including Apollo and LinkedIn to identify companies and contacts that match your criteria.',
  },
  {
    icon: UserPlus,
    title: 'Enrich Leads with Contact Data',
    description: 'I extract and verify emails, phone numbers, LinkedIn profiles, and other contact information for each decision maker.',
  },
  {
    icon: BarChart,
    title: 'Score Leads Based on Match',
    description: 'I calculate a qualification score for each lead based on how well they match your ICP criteria and buying signals.',
  },
  {
    icon: CheckCircle,
    title: 'Send to Pre-Qualified Pipeline',
    description: 'Leads that score above your threshold are moved to Pre-Qualified status for your review before final approval.',
  },
  {
    icon: Shield,
    title: 'Verify Before Delivery',
    description: 'I run final verification checks on contact data to ensure each lead is accurate and ready for your outreach campaigns.',
  },
];

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'behavior', label: 'Behavior', icon: Target },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

const LeadGenHowIWorkModal: React.FC<LeadGenHowIWorkModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('process');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Brain className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 font-display tracking-tight">How I Work</h3>
              <p className="text-sm text-gray-600 mt-1">Lead generation process, search behavior and intelligence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'text-[#1875FF] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center space-x-2">
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'process' && <ProcessTab />}
          {activeTab === 'behavior' && <BehaviorTab />}
          {activeTab === 'intelligence' && <IntelligenceTab />}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

const ProcessTab = () => (
  <div className="space-y-4 animate-in slide-in-from-left duration-300">
    {processSteps.map((step, index) => {
      const Icon = step.icon;
      return (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-[#1875FF]/30 transition-all hover:shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 self-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg">
                <Icon className="text-white" size={24} />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-400 mb-1">
                STEP {index + 1}: <span className="text-gray-900">{step.title}</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const BehaviorTab = () => (
  <div className="space-y-6 animate-in slide-in-from-left duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Search className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Search Parameters</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I search using your exact ICP criteria to find the best-fit prospects:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Target countries and geographic regions</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Industry verticals and sub-industries</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Company size ranges (employee count and revenue)</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Decision-maker titles and seniority levels</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Shield className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Data Verification Rules</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            Every lead goes through multiple verification checks before delivery:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Email address validation and deliverability check</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Phone number format and reachability verification</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>LinkedIn profile existence and current role confirmation</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Company data freshness and accuracy cross-referencing</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <BarChart className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Scoring Thresholds</h4>
          <p className="text-gray-700 leading-relaxed">
            Each lead receives a qualification score based on ICP match strength. Only leads scoring
            above your configured threshold advance to Pre-Qualified status. Borderline leads are
            flagged for manual review, ensuring you only invest time in high-potential prospects.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <RefreshCw className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Refresh Cadence</h4>
          <p className="text-gray-700 leading-relaxed">
            Lead data is re-verified periodically to ensure accuracy over time. Job titles, company
            information, and contact details are checked against source databases so your pipeline
            always contains current, actionable data.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">When I Stop Searching</h4>
          <p className="text-gray-700 leading-relaxed">
            I stop searching when: the requested number of leads has been fulfilled, all matching
            prospects in the database have been exhausted, or the remaining leads fall below your
            minimum quality threshold.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const IntelligenceTab = () => (
  <div className="space-y-6 animate-in slide-in-from-right duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <TrendingUp className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Lead Scoring Algorithm</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I score leads using multiple weighted factors to rank quality:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>ICP match:</strong> How closely the prospect fits your ideal customer profile</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Buying signals:</strong> Growth indicators, hiring patterns, tech stack changes</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Data completeness:</strong> Quality and availability of contact information</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Recency:</strong> How fresh the data is from source databases</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Layers className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Duplicate Detection</h4>
          <p className="text-gray-700 leading-relaxed">
            I identify and merge duplicate contacts across multiple databases using fuzzy matching
            on names, email addresses, company names, and LinkedIn profiles. This ensures your pipeline
            is clean and no prospect is contacted twice from different sources.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Shield className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Data Enrichment Quality</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            Each data point comes with a confidence level:
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
              <Flame size={14} className="text-red-500" />
              High Confidence
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
              <Sun size={14} />
              Medium
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
              <Snowflake size={14} />
              Low Confidence
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Pattern Learning</h4>
          <p className="text-gray-700 leading-relaxed">
            I learn from which leads your team approves and which convert into opportunities.
            Over time, I refine search criteria, scoring weights, and enrichment priorities to
            deliver increasingly higher-quality prospects that match your real conversion patterns.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default LeadGenHowIWorkModal;
